import { useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MODEL_LABEL } from '@fa/ui';
import { ApiError, api } from './lib/api.js';
import type { EmitterView } from './lib/types.js';

/**
 * Inutilização de faixa de numeração.
 *
 * Necessária quando números foram consumidos sem nota transmitida — queda de
 * energia no PDV, por exemplo. Sem inutilizar, a numeração fica com buraco e
 * o fisco cobra explicação no cruzamento.
 *
 * A justificativa tem mínimo de 15 caracteres porque a SEFAZ recusa menos que
 * isso; validar aqui evita uma ida perdida ao WebService.
 */
export function Inutilizacao({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (mensagem: string) => void;
}): ReactNode {
  const [emitterId, setEmitterId] = useState('');
  const [model, setModel] = useState<'nfe' | 'nfce'>('nfce');
  const [serie, setSerie] = useState(1);
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const emitters = useQuery({
    queryKey: ['emitters'],
    queryFn: () => api<{ data: EmitterView[] }>('/v1/emitters'),
  });

  const enviar = useMutation({
    mutationFn: () =>
      api<{ accepted: boolean; protocol: string | null; message: string }>('/v1/inutilizations', {
        method: 'POST',
        body: {
          emitter_id: emitterId,
          model,
          serie,
          number_from: Number(de),
          number_to: Number(ate),
          justification: justificativa,
        },
      }),
    onSuccess: (data) => {
      onDone(
        data.accepted
          ? `Faixa ${de}–${ate} inutilizada. Protocolo ${data.protocol ?? '—'}.`
          : `A SEFAZ recusou: ${data.message}`,
      );
      onClose();
    },
    onError: (err) => setErro(err instanceof ApiError ? err.message : 'Não foi possível inutilizar.'),
  });

  const faixaValida = Number(de) > 0 && Number(ate) >= Number(de);
  const pronto = emitterId && faixaValida && justificativa.trim().length >= 15;

  return (
    <div className="card">
      <div className="hd">
        <h2>Inutilizar faixa de numeração</h2>
        <button className="x" onClick={onClose} aria-label="Fechar">
          ×
        </button>
      </div>

      <div className="bd" style={{ display: 'grid', gap: 14 }}>
        <div className="alert info">
          <b>Quando usar</b>
          Quando números foram consumidos sem que a nota chegasse à SEFAZ. A faixa inutilizada não pode ser
          reaproveitada, e a operação não tem volta.
        </div>

        {erro && (
          <div className="alert bad">
            <b>Não foi possível inutilizar</b>
            {erro}
          </div>
        )}

        <div className="grid-2">
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--ink-2)' }}>Empresa</span>
            <select className="input" value={emitterId} onChange={(e) => setEmitterId(e.target.value)}>
              <option value="">Selecione</option>
              {(emitters.data?.data ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nome_fantasia ?? e.razao_social} — {e.uf}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--ink-2)' }}>Modelo</span>
            <select
              className="input"
              value={model}
              onChange={(e) => setModel(e.target.value as 'nfe' | 'nfce')}
            >
              <option value="nfce">{MODEL_LABEL.nfce}</option>
              <option value="nfe">{MODEL_LABEL.nfe}</option>
            </select>
          </label>
        </div>

        <div className="grid-3">
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--ink-2)' }}>Série</span>
            <input
              className="input"
              type="number"
              min={1}
              value={serie}
              onChange={(e) => setSerie(Number(e.target.value))}
            />
          </label>
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--ink-2)' }}>Do número</span>
            <input
              className="input"
              type="number"
              min={1}
              value={de}
              onChange={(e) => setDe(e.target.value)}
            />
          </label>
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--ink-2)' }}>Até o número</span>
            <input
              className="input"
              type="number"
              min={1}
              value={ate}
              onChange={(e) => setAte(e.target.value)}
            />
          </label>
        </div>

        <label style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--ink-2)' }}>
            Justificativa (mínimo 15 caracteres)
          </span>
          <textarea
            className="input"
            style={{ height: 64, padding: 8, minWidth: 0 }}
            value={justificativa}
            onChange={(e) => setJustificativa(e.target.value)}
            placeholder="Ex.: Queda de energia no PDV 3 durante a emissao"
          />
        </label>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-primary"
            disabled={!pronto || enviar.isPending}
            onClick={() => enviar.mutate()}
          >
            {enviar.isPending ? 'Enviando à SEFAZ...' : 'Inutilizar faixa'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
